import React, { useState } from "react";
import {
  Typography,
  Button,
  Autocomplete,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as Yup from "yup";
import tanque from "../../img/Ruta.jpg";
import routeData from "./Util/Rutas.json";
import "tailwindcss/tailwind.css";
import miau from "../../img/miau.jpeg";

export default function CalculoDistancia() {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [rutaActual, setRutaActual] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [vista, setVista] = useState('principal');

  const routeValidationSchema = Yup.object().shape({
    origin: Yup.string()
      .required("Debe seleccionar una ciudad de origen")
      .nullable(),
    destination: Yup.string()
      .required("Debe seleccionar una ciudad de destino")
      .nullable()
      .test(
        "valid-route",
        "No existe una ruta directa entre estas ciudades",
        function (value) {
          const { origin } = this.parent;
          if (!origin || !value) return true;
          return routeData.some(
            (route) =>
              (route.origen === origin && route.destino === value) ||
              (route.origen === value && route.destino === origin)
          );
        }
      ),
  });

  const validateRoute = async (origin, destination) => {
    try {
      await routeValidationSchema.validate(
        { origin, destination },
        { abortEarly: false }
      );
      setValidationErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
      setValidationErrors(errors);
      return false;
    }
  };

  const uniqueOrigins = [
    ...new Set([
      ...routeData.map((ruta) => ruta.origen),
      ...routeData.map((ruta) => ruta.destino),
    ]),
  ].sort();

  const availableDestinations = selectedOrigin
    ? [
        ...new Set([
          ...routeData
            .filter(
              (ruta) =>
                ruta.origen === selectedOrigin ||
                ruta.destino === selectedOrigin
            )
            .map((ruta) =>
              ruta.origen === selectedOrigin ? ruta.destino : ruta.origen
            ),
        ]),
      ].sort()
    : [];

  const handleDestinationChange = async (event, newValue) => {
    const isValid = await validateRoute(selectedOrigin, newValue);
    if (isValid) {
      setSelectedDestination(newValue);
      if (newValue && selectedOrigin) {
        const rutasEncontradas = routeData.filter(
          (ruta) =>
            (ruta.origen === selectedOrigin && ruta.destino === newValue) ||
            (ruta.origen === newValue && ruta.destino === selectedOrigin)
        );
        const rutaOptima = rutasEncontradas.reduce((prev, current) =>
          prev.kilometraje < current.kilometraje ? prev : current
        );
        setRutaActual(rutaOptima || null);
      } else {
        setRutaActual(null);
      }
    }
  };

  const handleOriginChange = async (event, newValue) => {
    setSelectedOrigin(newValue);
    setSelectedDestination(null);
    setRutaActual(null);
    setValidationErrors({});
  };

  const formatearRuta = (rutaCompleta, origen, destino) => {
    if (!rutaCompleta) return [];
    const lineas = rutaCompleta.split("\n").filter((linea) => linea.trim());
    const indicePasos = lineas.findIndex((linea) => linea.includes("- "));
    let pasos = lineas.slice(indicePasos).filter((paso) => paso.startsWith("- "));

    // Determine if the route needs to be reversed
    const rutaInvertida = rutaActual.origen === destino && rutaActual.destino === origen;
    if (rutaInvertida) {
      pasos = pasos.reverse();
    }

    const rutaFormateada = pasos.map((paso) => paso.replace("- ", ""));
    return rutaFormateada;
  };

  return (
    <div>
      <div className="flex flex-col items-center justify-center w-full bg-cover bg-center bg-no-repeat bg-[#fba419]">
        <br />
        <div className="flex items-center">
          <img
            src={tanque}
            alt="Tanque de Gasolina"
            className="w-16 h-16 mr-4 rounded-2xl"
          />
          <Typography
            variant={isLargeScreen ? "h3" : "h6"}
            className="text-white font-bold text-center"
          >
            Obtener distancia de una Ruta
          </Typography>
        </div>
        <br />
      </div>

      <div className="flex min-h-screen bg-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full p-8 bg-white rounded-lg shadow-lg">
          <div className="col-span-1">
            <Typography
              variant="h4"
              className="text-center mb-6 text-gray-800 font-bold"
            >
              INGRESE LA INFORMACION
            </Typography>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Box>
                <Autocomplete
                  className="mt-8"
                  options={uniqueOrigins}
                  value={selectedOrigin}
                  onChange={handleOriginChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ciudad Origen"
                      error={!!validationErrors.origin}
                      helperText={validationErrors.origin}
                    />
                  )}
                />
              </Box>
              <Box>
                <Autocomplete
                  className="mt-8"
                  options={availableDestinations}
                  value={selectedDestination}
                  onChange={handleDestinationChange}
                  disabled={!selectedOrigin}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ciudad Destino"
                      error={!!validationErrors.destination}
                      helperText={validationErrors.destination}
                    />
                  )}
                />
              </Box>
            </div>
          </div>

          <div style={{ marginBottom: "0px" }}>
            {rutaActual && !isLargeScreen ? (
              <div className="rounded-lg mt-[-100px]">
                {vista === 'principal' ? (
                  <div className="flex flex-col gap-4">
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ backgroundColor: "#096eff", color: "#fff" }}
                      fullWidth
                      onClick={() => setVista('info')}
                    >
                      Obtener Información
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ backgroundColor: "#096eff", color: "#fff" }}
                      fullWidth
                      onClick={() => setVista('ruta')}
                    >
                      Ver Ruta
                    </Button>
                  </div>
                ) : vista === 'info' ? (
                  <div className="fixed top-0 left-0 w-full h-full bg-white z-50 overflow-auto p-4">
                    <div>
                      <div className="grid grid-cols-1 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg">
                          <Typography variant="subtitle2">
                            Origen
                          </Typography>
                          <Typography variant="h6">
                            {selectedOrigin}
                          </Typography>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <Typography variant="subtitle2">
                            Destino
                          </Typography>
                          <Typography variant="h6">
                            {selectedDestination}
                          </Typography>
                        </div>
                      </div>

                      <div className="bg-black-50 p-4 rounded-lg mb-6">
                        <Typography variant="subtitle2" className="text-black-600 font-medium">
                          Distancia Total
                        </Typography>
                        <Typography variant="h4" className="font-bold">
                          {rutaActual.kilometraje.toLocaleString()} km
                        </Typography>
                      </div>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setVista('principal')}
                      className="mb-4 mt-8 w-full"
                      sx={{ backgroundColor: "#096eff", color: "#fff" }}
                    >
                      Atrás
                    </Button>
                  </div>
                ) : (
                  <div className="fixed top-0 left-0 w-full h-full bg-white z-50 overflow-auto p-4">
                    <div>
                      <div className="mt-6">
                        <Typography
                          variant="subtitle1"
                          className="font-semibold text-black mb-3 text-center"
                          style={{ fontSize: "19px", whiteSpace: "nowrap" }}
                        >
                          Mejor Ruta a Seguir en ambos Sentidos
                        </Typography>
                        <Typography className="m-2 text-center" style={{ fontSize: "15px", marginLeft: "10px", marginBottom: "10px" }}>
                          <strong>
                            DE {selectedOrigin} A {selectedDestination}
                          </strong>
                        </Typography>
                        <div className="space-y-2 bg-white p-4 rounded-lg">
                          {formatearRuta(rutaActual.ruta_completa, selectedOrigin, selectedDestination).map((paso, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="min-w-[24px] h-6 bg-black-100 rounded-full flex items-center justify-center">
                                <Typography variant="body2" className="text-black-600">
                                  -
                                </Typography>
                              </div>
                              <Typography variant="body2" className="text-gray-600">
                                {paso}
                              </Typography>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Typography className="text-center" style={{ fontSize: "15px",  marginLeft: "10px" }}>
                        <strong>
                          DE {selectedDestination} A {selectedOrigin}
                        </strong>
                      </Typography>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setVista('principal')}
                      sx={{ backgroundColor: "#096eff", color: "#fff" }}
                      className="mb-4 mt-8 w-full"
                    >
                      Atrás
                    </Button>
                  </div>
                )}
              </div>
            ) : rutaActual ? (
              <div className="rounded-lg">
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg">
                      <Typography variant="subtitle2">
                        Origen
                      </Typography>
                      <Typography variant="h5">
                        {selectedOrigin}
                      </Typography>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <Typography variant="subtitle2">
                        Destino
                      </Typography>
                      <Typography variant="h5">
                        {selectedDestination}
                      </Typography>
                    </div>
                  </div>

                  <div className="bg-black-50 p-4 rounded-lg mb-6">
                    <Typography variant="subtitle2" className="text-black-600 font-medium">
                      Distancia Total
                    </Typography>
                    <Typography variant="h4" className="text-black-700 font-bold">
                      {rutaActual.kilometraje.toLocaleString()} km
                    </Typography>
                  </div>

                  <div className="mt-6 ml-6">
                    <Typography variant="subtitle1" className="font-semibold ml-6 mb-3 text-center">
                      Mejor Ruta a Seguir en ambos Sentidos
                    </Typography>
                    <Typography variant="subtitle2" className="text-center">
                      <strong>
                        DE {selectedOrigin} A {selectedDestination}
                      </strong>
                    </Typography>
                    <div className="space-y-2 bg-white p-4 rounded-lg mx-auto w-full max-w-[500px]">
                      {formatearRuta(rutaActual.ruta_completa, selectedOrigin, selectedDestination).map((paso, index) => (
                        <div key={index} className="flex items-center justify-start space-x-3">
                          <div className="min-w-[24px] h-6 bg-black-100 rounded-full flex items-center justify-center">
                            <Typography variant="body2" className="text-black-600 font-medium">
                              -
                            </Typography>
                          </div>
                          <Typography variant="body2" className="text-gray-600">
                            {paso}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Typography variant="subtitle2" className="text-center">
                      <strong>
                        DE {selectedDestination} A {selectedOrigin}
                      </strong>
                    </Typography>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {isLargeScreen && (
                  <div className="flex flex-col items-center justify-start h-full">
                    <img
                      src={miau}
                      alt="Miau"
                      style={{ height: "70px", width: "80px" }}
                    />
                    <Typography variant="h6" className="text-gray-800 font-bold mb-4">
                      Los valores se generan en KM para las distancias.
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
